import React, { ChangeEvent, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ModalHelpText, ModalTitle, StyledOption, StyledSelect } from "../../shared/styles";
import { ValueTypes } from "../../shared/types";
import { messages } from "./messages";
import {
  ChangeValueContainer,
  Container,
  InputContainer,
  StyledBox,
  StyledCheckbox,
  StyledInput,
} from "./styles";

interface Props {
  valueType: ValueTypes;
  valueTypes: ValueTypes[];
  canChangeValueType: boolean;
  onChangeValueType: (valueType: ValueTypes) => void;
  onChangeOperator: (operator: string) => void;
  onChangeValue: (value: number | boolean) => void;
  errors: string[];
  isBlockingDay?: boolean;
}

const CHECKBOX_VALUES = [ValueTypes.closed, ValueTypes.closeToDeparture, ValueTypes.closeToArrival];

export const UpdateValue = ({
  valueType,
  valueTypes,
  canChangeValueType,
  onChangeValueType,
  onChangeOperator,
  onChangeValue,
  errors,
  isBlockingDay,
}: Props): JSX.Element => {
  const intl = useIntl();

  const isCheckbox = useMemo(() => CHECKBOX_VALUES.includes(valueType), [valueType]);

  const availableValueTypes = useMemo(() => {
    return valueTypes.map(value => (
      <StyledOption key={value} value={value}>
        {intl.formatMessage(messages[value])}
      </StyledOption>
    ));
  }, [valueTypes, intl]);

  const handleOnChangeValue = useCallback(
    (value: boolean | string) => {
      const finalValue = typeof value === "boolean" ? value : parseFloat(value);
      onChangeValue(finalValue);
    },
    [onChangeValue]
  );

  return (
    <Container>
      {isBlockingDay && (
        <ModalHelpText>
          <FormattedMessage {...messages.helpText} />
        </ModalHelpText>
      )}
      <ModalTitle>
        <FormattedMessage {...messages.change} />
      </ModalTitle>

      <ChangeValueContainer>
        {!canChangeValueType && valueType && (
          <StyledBox>
            <FormattedMessage {...messages[valueType]} />
          </StyledBox>
        )}

        {canChangeValueType && (
          <div>
            <StyledSelect
              aria-label="value-type"
              value={valueType}
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                onChangeValueType(ValueTypes[event.target.value as keyof typeof ValueTypes])
              }>
              {availableValueTypes}
            </StyledSelect>
          </div>
        )}

        <div>
          {!isCheckbox && (
            <StyledSelect
              aria-label="operator"
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                onChangeOperator(event.target.value)
              }>
              <StyledOption value="=">{intl.formatMessage(messages.setValue)}</StyledOption>
              <StyledOption value="+">{intl.formatMessage(messages.increaseUnits)}</StyledOption>
              <StyledOption value="-">{intl.formatMessage(messages.decreaseUnits)}</StyledOption>
              {/** TODO: add increase and decrease percentage */}
            </StyledSelect>
          )}
        </div>

        <InputContainer>
          {isCheckbox && (
            <StyledCheckbox
              type="checkbox"
              placeholder={intl.formatMessage(messages.introduceValue)}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleOnChangeValue(event.target.checked)
              }
            />
          )}

          {!isCheckbox && (
            <StyledInput
              type="text"
              placeholder={intl.formatMessage(messages.introduceValue)}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleOnChangeValue(event.target.value)
              }
              error={errors.includes("value")}
            />
          )}
        </InputContainer>
      </ChangeValueContainer>
    </Container>
  );
};
